// === ICLOUD PHOTO INTEGRATION === //
// System to fetch photos from iCloud shared albums and photo streams

class iCloudPhotoManager {
    constructor() {
        this.albumCache = new Map();
        this.retryAttempts = 3;
        this.cacheDuration = 300000; // 5 minutes
    }

    // Extract album ID from various iCloud URL formats
    extractAlbumId(url) {
        // Handle shared albums: https://www.icloud.com/sharedalbum/#B0...
        const sharedAlbumMatch = url.match(/sharedalbum\/#(.+)/);
        if (sharedAlbumMatch) {
            return { type: 'sharedalbum', id: sharedAlbumMatch[1] };
        }
        
        // Handle photo streams: https://share.icloud.com/photos/...
        const photoStreamMatch = url.match(/share\.icloud\.com\/photos\/(.+)/);
        if (photoStreamMatch) {
            return { type: 'photostream', id: photoStreamMatch[1] };
        }
        
        return null;
    }

    // Check if album data is cached and still valid
    getCachedAlbum(albumId) {
        const cached = this.albumCache.get(albumId);
        if (cached && (Date.now() - cached.timestamp) < this.cacheDuration) {
            return cached.data;
        }
        return null;
    }

    // Cache album data
    cacheAlbum(albumId, data) {
        this.albumCache.set(albumId, {
            data: data,
            timestamp: Date.now()
        });
    }

    // Create placeholder photos for iCloud albums that can't be directly accessed
    createPlaceholderPhotos(albumName, count = 5) {
        const placeholders = [];
        const albumInfo = {
            family: {
                titles: ["Family Beach Day", "Picnic in the Park", "Movie Night", "Cooking Together", "Weekend Adventure"],
                descriptions: [
                    "We had so much fun at the beach building sandcastles! 🏖️",
                    "Perfect weather for our family picnic with sandwiches and games! 🧺",
                    "Cozy movie night with popcorn and my favorite animated film! 🍿",
                    "Helping Mom make cookies - I'm getting really good at mixing! 🍪",
                    "Exploring the hiking trail and finding cool rocks! 🥾"
                ]
            },
            school: {
                titles: ["First Day Excitement", "Science Fair Project", "Art Class Creation", "Field Trip Fun", "Reading Achievement"],
                descriptions: [
                    "My first day of school was amazing - I made so many new friends! 📚",
                    "My volcano science project erupted perfectly! Everyone was impressed! 🌋",
                    "I painted the most beautiful rainbow in art class today! 🌈",
                    "The museum field trip was incredible - dinosaurs are so cool! 🦕",
                    "I read 10 books this month and got a special certificate! 📖"
                ]
            },
            hobbies: {
                titles: ["Dance Recital", "Soccer Practice", "Art Creation", "Music Lesson", "Reading Time"],
                descriptions: [
                    "My dance recital performance - I remembered all the moves! 💃",
                    "Scored my first goal at soccer practice today! ⚽",
                    "Created this masterpiece with my new art supplies! 🎨",
                    "Learning to play my favorite song on the piano! 🎹",
                    "Reading my favorite adventure book in my cozy corner! 📚"
                ]
            },
            special: {
                titles: ["Birthday Celebration", "Holiday Magic", "Family Vacation", "Achievement Day", "Special Surprise"],
                descriptions: [
                    "My amazing birthday party with all my friends and a unicorn cake! 🦄🎂",
                    "Christmas morning was pure magic - Santa brought everything! 🎄✨",
                    "Our family vacation was the best adventure ever! 🏖️",
                    "I won first place in the art contest - I'm so proud! 🏆",
                    "Mom and Dad surprised me with a special day just for me! 🎁"
                ]
            }
        };

        const info = albumInfo[albumName] || albumInfo.family;
        
        for (let i = 0; i < count; i++) {
            placeholders.push({
                id: `${albumName}_${i + 1}`,
                src: `https://picsum.photos/800/600?random=${Date.now() + i}`,
                thumbnail: `https://picsum.photos/300/200?random=${Date.now() + i}`,
                title: info.titles[i] || `Special Moment ${i + 1}`,
                description: info.descriptions[i] || `A wonderful memory from our ${albumName} collection! ✨`,
                date: new Date(Date.now() - (i * 86400000)).toISOString().split('T')[0],
                location: "Family Memories",
                tags: [albumName, "memories", "family"],
                photographer: "Family",
                featured: i === 0,
                allowDownload: true,
                source: "icloud",
                originalUrl: WEBSITE_CONFIG.photos.icloudAlbums[albumName]
            });
        }

        return placeholders;
    }

    // Load photos from iCloud URL
    async loadAlbumPhotos(albumName, url) {
        try {
            // Check cache first
            const cached = this.getCachedAlbum(url);
            if (cached) {
                return cached;
            }

            const albumInfo = this.extractAlbumId(url);
            if (!albumInfo) {
                console.warn(`Invalid iCloud URL format: ${url}`);
                return this.createPlaceholderPhotos(albumName);
            }

            // For now, create placeholders since direct iCloud API access requires authentication
            // In a real implementation, you'd need to use Apple's PhotoKit or similar
            console.log(`Loading iCloud ${albumInfo.type} for ${albumName}:`, url);
            
            // Show user-friendly message
            this.showICloudNotice(albumName, url);
            
            const photos = this.createPlaceholderPhotos(albumName);
            this.cacheAlbum(url, photos);
            
            return photos;

        } catch (error) {
            console.error(`Error loading iCloud album ${albumName}:`, error);
            return this.createPlaceholderPhotos(albumName);
        }
    }

    // Show notice about iCloud integration
    showICloudNotice(albumName, url) {
        const notice = document.createElement('div');
        notice.className = 'icloud-notice';
        notice.innerHTML = `
            <div class="notice-content">
                <div class="notice-icon">☁️</div>
                <div class="notice-text">
                    <h4>iCloud Album Connected!</h4>
                    <p>Your ${albumName} album is linked to iCloud. Click below to view the actual photos:</p>
                    <a href="${url}" target="_blank" rel="noopener noreferrer" class="btn btn-primary">
                        <i class="fas fa-external-link-alt"></i>
                        View on iCloud
                    </a>
                </div>
                <button class="notice-close">&times;</button>
            </div>
        `;

        // Style the notice
        notice.style.cssText = `
            position: fixed;
            top: 80px;
            right: 20px;
            background: white;
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.2);
            padding: 20px;
            max-width: 300px;
            z-index: 10000;
            animation: slideInRight 0.3s ease;
        `;

        document.body.appendChild(notice);

        // Auto-remove after 10 seconds
        setTimeout(() => {
            if (notice.parentNode) {
                notice.style.animation = 'slideOutRight 0.3s ease';
                setTimeout(() => {
                    if (notice.parentNode) {
                        notice.parentNode.removeChild(notice);
                    }
                }, 300);
            }
        }, 10000);

        // Close button
        notice.querySelector('.notice-close').addEventListener('click', () => {
            if (notice.parentNode) {
                notice.parentNode.removeChild(notice);
            }
        });
    }

    // Update gallery to use iCloud photos
    async updateGalleryWithiCloudPhotos() {
        if (!WEBSITE_CONFIG.photos || WEBSITE_CONFIG.photos.storageMethod !== 'icloud') {
            return;
        }

        console.log('🍎 Loading iCloud photo albums...');

        const icloudAlbums = WEBSITE_CONFIG.photos.icloudAlbums;
        const updatedDatabase = {};

        for (const [albumName, url] of Object.entries(icloudAlbums)) {
            if (url && url !== "https://www.icloud.com/sharedalbum/#B0...") {
                console.log(`Loading ${albumName} from iCloud:`, url);
                const photos = await this.loadAlbumPhotos(albumName, url);
                updatedDatabase[albumName] = photos;
            }
        }

        // Update global photo database
        if (typeof window !== 'undefined') {
            window.PHOTO_DATABASE = { ...window.PHOTO_DATABASE, ...updatedDatabase };
        }

        console.log('✅ iCloud albums loaded successfully!');
        return updatedDatabase;
    }

    // Manual refresh function
    async refreshAlbum(albumName) {
        const url = WEBSITE_CONFIG.photos.icloudAlbums[albumName];
        if (url) {
            // Clear cache
            this.albumCache.delete(url);
            // Reload
            const photos = await this.loadAlbumPhotos(albumName, url);
            
            // Update UI
            if (typeof window.galleryManager !== 'undefined') {
                window.galleryManager.albums.set(albumName, {
                    title: ALBUM_CONFIG[albumName]?.title || `${albumName} Album`,
                    photos: photos
                });
            }

            return photos;
        }
    }
}

// Add CSS for iCloud notices
const icloudStyles = `
    @keyframes slideInRight {
        from {
            transform: translateX(100%);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }
    
    @keyframes slideOutRight {
        from {
            transform: translateX(0);
            opacity: 1;
        }
        to {
            transform: translateX(100%);
            opacity: 0;
        }
    }
    
    .icloud-notice {
        font-family: var(--font-body);
    }
    
    .notice-content {
        display: flex;
        align-items: flex-start;
        gap: 15px;
    }
    
    .notice-icon {
        font-size: 2rem;
        flex-shrink: 0;
    }
    
    .notice-text h4 {
        margin: 0 0 8px 0;
        color: var(--primary-purple);
        font-family: var(--font-heading);
    }
    
    .notice-text p {
        margin: 0 0 15px 0;
        font-size: 0.9rem;
        line-height: 1.4;
    }
    
    .notice-close {
        position: absolute;
        top: 10px;
        right: 15px;
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        color: var(--gray);
    }
    
    .notice-close:hover {
        color: var(--primary-pink);
    }
`;

// Add styles to page
const icloudStyleSheet = document.createElement('style');
icloudStyleSheet.textContent = icloudStyles;
document.head.appendChild(icloudStyleSheet);

// Initialize iCloud manager
document.addEventListener('DOMContentLoaded', async () => {
    window.icloudManager = new iCloudPhotoManager();
    
    // Load iCloud photos if using iCloud storage
    if (WEBSITE_CONFIG.photos?.storageMethod === 'icloud') {
        await window.icloudManager.updateGalleryWithiCloudPhotos();
        
        // Trigger gallery refresh
        if (typeof window.galleryManager !== 'undefined') {
            window.galleryManager.loadAlbumData();
        }
    }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = iCloudPhotoManager;
}
